/**
 * CDP SDK Identity Management
 * Anonymous ID and User ID management
 */

import { STORAGE_KEYS } from './types';
import { generateId, log } from './utils';
import { getCookie, setCookie, deleteCookie, setHybrid, getHybrid, deleteHybrid, getSessionStorage, setSessionStorage } from './storage';

// =============================================================================
// EPHEMERAL ID (anonymous-until-consent lite posture)
// =============================================================================

const EPHEMERAL_ID_KEY = 'cdp_eph_id';

/**
 * Get or create an EPHEMERAL visitor ID for the lite (pre-consent) posture.
 * Stored only in sessionStorage, so it gives per-tab-session continuity (page
 * views in one visit group together) but is NOT a persistent, cross-session
 * identifier — it vanishes when the tab closes and is never written to a cookie.
 * On consent the SDK switches to a real, persistent anonymous ID.
 */
export function getOrCreateEphemeralId(): string {
  let id = getSessionStorage<string>(EPHEMERAL_ID_KEY);
  if (!id) {
    id = generateId();
    setSessionStorage(EPHEMERAL_ID_KEY, id);
  }
  return id;
}

// =============================================================================
// ANONYMOUS ID
// =============================================================================

/**
 * Get or create an anonymous ID
 * Anonymous ID persists across sessions until reset
 */
export function getOrCreateAnonymousId(
  cookieExpiration: number = 365,
  cookieDomain?: string
): string {
  // Try to get existing ID from cookie/storage
  let anonymousId = getHybrid(STORAGE_KEYS.ANONYMOUS_ID);

  if (!anonymousId) {
    // Generate new ID
    anonymousId = generateId();
    log('Generated new anonymous ID:', anonymousId);
  }

  // Always refresh storage to extend expiration
  setHybrid(STORAGE_KEYS.ANONYMOUS_ID, anonymousId, cookieExpiration, cookieDomain);

  return anonymousId;
}

/**
 * Get the current anonymous ID without creating one
 */
export function getAnonymousId(): string | null {
  return getHybrid(STORAGE_KEYS.ANONYMOUS_ID);
}

/**
 * Set a specific anonymous ID (for migration scenarios)
 */
export function setAnonymousId(
  anonymousId: string,
  cookieExpiration: number = 365,
  cookieDomain?: string
): void {
  setHybrid(STORAGE_KEYS.ANONYMOUS_ID, anonymousId, cookieExpiration, cookieDomain);
  log('Anonymous ID set:', anonymousId);
}

/**
 * Clear the anonymous ID and generate a new one
 */
export function resetAnonymousId(
  cookieExpiration: number = 365,
  cookieDomain?: string
): string {
  // Delete old ID
  deleteHybrid(STORAGE_KEYS.ANONYMOUS_ID, cookieDomain);

  // Generate and store new ID
  const newId = generateId();
  setHybrid(STORAGE_KEYS.ANONYMOUS_ID, newId, cookieExpiration, cookieDomain);

  log('Anonymous ID reset, new ID:', newId);
  return newId;
}

// =============================================================================
// USER ID
// =============================================================================

/**
 * Get the current user ID
 */
export function getUserId(): string | null {
  return getHybrid(STORAGE_KEYS.USER_ID);
}

/**
 * Set the user ID (called during identify)
 */
export function setUserId(
  userId: string,
  cookieExpiration: number = 365,
  cookieDomain?: string
): void {
  setHybrid(STORAGE_KEYS.USER_ID, userId, cookieExpiration, cookieDomain);
  log('User ID set:', userId);
}

/**
 * Clear the user ID
 */
export function clearUserId(cookieDomain?: string): void {
  deleteHybrid(STORAGE_KEYS.USER_ID, cookieDomain);
  log('User ID cleared');
}

// =============================================================================
// COMBINED OPERATIONS
// =============================================================================

/**
 * Reset all identity data (both anonymous and user ID)
 * Generates a new anonymous ID
 */
export function resetIdentity(
  cookieExpiration: number = 365,
  cookieDomain?: string
): string {
  // Clear user ID
  clearUserId(cookieDomain);

  // Reset anonymous ID (generates new one)
  return resetAnonymousId(cookieExpiration, cookieDomain);
}

/**
 * Get current identity state
 */
export function getIdentityState(): {
  anonymousId: string | null;
  userId: string | null;
  isIdentified: boolean;
} {
  const anonymousId = getAnonymousId();
  const userId = getUserId();

  return {
    anonymousId,
    userId,
    isIdentified: userId !== null,
  };
}

// =============================================================================
// ALIAS (for future use)
// =============================================================================

/**
 * Create an alias between two IDs
 * This is used when a user signs up and we want to link
 * their anonymous activity to their new user ID
 */
export function createAlias(
  previousId: string,
  newId: string
): { previousId: string; newId: string } {
  log('Alias created:', previousId, '->', newId);
  return { previousId, newId };
}
